import React from "react";
import {Box, Typography, Button, Grid, Card, CardContent, ThemeProvider, createTheme, Fade, Zoom, Link} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {NavBar} from "./Navbar";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AddLocationIcon from "@mui/icons-material/AddLocation";
import StarIcon from "@mui/icons-material/Star";
import CategoryIcon from "@mui/icons-material/Category";
import HomeIcon from "@mui/icons-material/Home";
import EventIcon from "@mui/icons-material/Event";

const theme = createTheme({
  typography: {
    fontFamily: "'Inter', sans-serif",
    h1: {
      fontFamily: "'Merriweather', serif",
      fontWeight: 900,
    },
    h2: {
      fontFamily: "'Merriweather', serif",
      fontWeight: 700,
    },
    h3: {
      fontFamily: "'Merriweather', serif",
      fontWeight: 700,
    },
    h4: {
      fontFamily: "'Merriweather', serif",
      fontWeight: 600,
    },
    h5: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
    },
    h6: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
    },
    button: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
    },
    body1: {
      fontFamily: "'Inter', sans-serif",
    },
    body2: {
      fontFamily: "'Inter', sans-serif",
    },
  },
});

const LandingPage = ({
  user,
  handleLogin,
  handleLogout,
  setAutocomplete,
  handleCitySearch,
}) => {
  const navigate = useNavigate();

  const features = [
    {
      icon: "map",
      iconType: "image",
      title: "Interactive Map",
      description:
        "Explore Christmas light displays on an interactive map with custom markers",
    },
    {
      icon: "trophy",
      iconType: "mui",
      title: "Top Picks",
      description: "See the most popular displays based on community votes",
    },
    {
      icon: "plan",
      iconType: "image",
      title: "Route Planning",
      description: "Plan the perfect tour with optimized routes",
    },
    {
      icon: "addLocation",
      iconType: "mui",
      title: "Add Locations",
      description: "Share your favorite displays with the community",
    },
    {
      icon: "star",
      iconType: "mui",
      title: "Reviews & Ratings",
      description: "Read reviews and rate your favorite displays",
    },
    {
      icon: "category",
      iconType: "mui",
      title: "Multiple Categories",
      description: "Find houses, events, markets, and community displays",
    },
  ];

  const steps = [
    {
      number: "1",
      title: "Explore the Map",
      description:
        "Browse Christmas light displays in your area on an interactive map",
    },
    {
      number: "2",
      title: "Plan Your Route",
      description: "Select multiple locations and get an optimized route",
    },
    {
      number: "3",
      title: "Add Your Display",
      description: "Share your house or event with the community - it's quick and easy!",
    },
    {
      number: "4",
      title: "Vote & Enjoy",
      description: "Rate your favorite displays and help others discover the best spots",
    },
  ];

  return (
    <ThemeProvider theme={theme}>
    <Box sx={{minHeight: "100vh", backgroundColor: "#FAFAFA"}}>
      <NavBar
        user={user}
        handleLogin={handleLogin}
        handleLogout={handleLogout}
        navigate={navigate}
        setAutocomplete={setAutocomplete}
        handleCitySearch={handleCitySearch}
      />
      {/* Hero Section - Matching Screenshot Design */}
      <Box
        sx={{
          backgroundImage: "url('/background.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          color: "#333",
          padding: {xs: "60px 20px", sm: "80px 40px", md: "100px 60px"},
          textAlign: "center",
          position: "relative",
          minHeight: {xs: "450px", sm: "500px", md: "550px"},
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            zIndex: 1,
          },
        }}
      >
        <Box sx={{ 
          position: "relative", 
          width: "100%", 
          maxWidth: "900px",
          margin: "0 auto",
          textAlign: "center",
          zIndex: 2,
        }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: {xs: "2.5rem", sm: "3.5rem", md: "4rem"},
            fontWeight: 700,
            marginBottom: "24px",
            fontFamily: "'Merriweather', serif",
            color: "#C41E3A",
            lineHeight: 1.2,
            display: "block",
          }}
        >
          <Box component="span" sx={{ display: "block" }}>Christmas Lights</Box>
          <Box component="span" sx={{ display: "block" }}>Map</Box>
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontSize: {xs: "1rem", sm: "1.2rem", md: "1.4rem"},
            fontWeight: 400,
            marginBottom: "20px",
            maxWidth: "700px",
            margin: "0 auto 20px",
            color: "#666",
            lineHeight: 1.5,
            fontFamily: "'Inter', sans-serif",
          }}
        >
          Discover and explore the most beautiful holiday light displays in your area.
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: {xs: "0.9rem", sm: "1rem"},
            fontWeight: 400,
            marginBottom: "32px",
            maxWidth: "600px",
            margin: "0 auto 32px",
            color: "#666",
            lineHeight: 1.6,
            fontFamily: "'Inter', sans-serif",
          }}
        >
          Find the best Christmas light displays in your area. Browse locations, read reviews, and plan your perfect holiday tour.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate("/map")}
          sx={{
            backgroundColor: "#C41E3A",
            color: "#FFFFFF",
            padding: {xs: "14px 32px", sm: "16px 40px"},
            fontSize: {xs: "1rem", sm: "1.1rem"},
            fontWeight: 600,
            borderRadius: "8px",
            textTransform: "none",
            boxShadow: "none",
            position: "relative",
            zIndex: 10,
            fontFamily: "'Inter', sans-serif",
            "&:hover": {
              backgroundColor: "#DC143C",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(196, 30, 58, 0.3)",
            },
            transition: "all 0.3s ease",
          }}
        >
          Explore the Map →
        </Button>
        </Box>
      </Box>

      {/* Divider Line */}
      <Box
        sx={{
          width: "100%",
          height: "1px",
          backgroundColor: "#E0E0E0",
          margin: "0 auto",
        }}
      />

      {/* Features Section */}
      <Box
        sx={{
          padding: {xs: "60px 20px", sm: "80px 40px", md: "100px 60px"},
          maxWidth: "1200px",
          margin: "0 auto",
          backgroundColor: "#FFFFFF",
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontSize: {xs: "2rem", sm: "2.5rem", md: "3rem"},
            fontWeight: 700,
            textAlign: "center",
            marginBottom: "16px",
            color: "#C41E3A",
            fontFamily: "'Merriweather', serif",
          }}
        >
          Everything You Need
        </Typography>
        <Typography
          variant="h6"
          sx={{
            textAlign: "center",
            marginBottom: "48px",
            color: "#666",
            fontWeight: 400,
            fontFamily: "'Inter', sans-serif",
          }}
        >
          Plan the perfect Christmas lights tour
        </Typography>

        <Grid container spacing={3}>
          {features.map((feature, index) => {
            const isAddLocations = feature.title === "Add Locations";
            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  onClick={isAddLocations ? () => navigate("/add-house") : undefined}
                  sx={{
                    height: "100%",
                    borderRadius: "16px",
                    position: "relative",
                    boxShadow: isAddLocations 
                      ? "0 4px 20px rgba(196, 30, 58, 0.25)" 
                      : "0 2px 8px rgba(0, 0, 0, 0.1)",
                    transition: "all 0.3s ease",
                    border: isAddLocations 
                      ? "2px solid #C41E3A" 
                      : "1px solid transparent",
                    backgroundColor: isAddLocations 
                      ? "linear-gradient(135deg, rgba(196, 30, 58, 0.05) 0%, rgba(196, 30, 58, 0.1) 100%)" 
                      : "#fff",
                    cursor: isAddLocations ? "pointer" : "default",
                    "&:hover": {
                      transform: isAddLocations ? "translateY(-6px)" : "translateY(-4px)",
                      boxShadow: isAddLocations
                        ? "0 8px 32px rgba(196, 30, 58, 0.4)"
                        : "0 6px 20px rgba(0, 0, 0, 0.15)",
                      border: isAddLocations 
                        ? "2px solid #C41E3A" 
                        : "1px solid rgba(196, 30, 58, 0.3)",
                      cursor: "pointer",
                    },
                  }}
                >
                  {isAddLocations && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "12px",
                        right: "12px",
                        backgroundColor: "#C41E3A",
                        color: "#FFFFFF",
                        padding: "4px 12px",
                        borderRadius: "12px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        fontFamily: "'Merriweather', serif",
                        boxShadow: "0 2px 8px rgba(196, 30, 58, 0.3)",
                      }}
                    >
                      Click to Add
                    </Box>
                  )}
                <CardContent sx={{padding: "32px"}}>
                  {feature.iconType === "image" ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginBottom: "16px",
                        minHeight: "100px",
                      }}
                    >
                      <img
                        src={`/${feature.icon}-icon.png`}
                        alt={feature.title}
                        style={{
                          width: "100px",
                          height: "100px",
                          objectFit: "contain",
                          display: "block",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </Box>
                  ) : feature.iconType === "mui" ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        marginBottom: "16px",
                      }}
                    >
                      {feature.icon === "trophy" && (
                        <EmojiEventsIcon sx={{ fontSize: "64px", color: "#C41E3A" }} />
                      )}
                      {feature.icon === "addLocation" && (
                        <AddLocationIcon sx={{ fontSize: "64px", color: "#C41E3A" }} />
                      )}
                      {feature.icon === "star" && (
                        <StarIcon sx={{ fontSize: "64px", color: "#C41E3A" }} />
                      )}
                      {feature.icon === "category" && (
                        <CategoryIcon sx={{ fontSize: "64px", color: "#C41E3A" }} />
                      )}
                    </Box>
                  ) : (
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: "3rem",
                        textAlign: "center",
                        marginBottom: "16px",
                      }}
                    >
                      {feature.icon}
                    </Typography>
                  )}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        marginBottom: "12px",
                        textAlign: "center",
                        color: "#333",
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        textAlign: "center",
                        lineHeight: 1.6,
                      }}
                    >
                      {feature.description}
                    </Typography>
                    {!isAddLocations && (
                      <Box 
                        sx={{
                          marginTop: "16px", 
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#999",
                            fontStyle: "italic",
                            fontSize: "0.75rem",
                            fontFamily: "'Merriweather', serif",
                          }}
                        >
                          Available on map
                        </Typography>
                      </Box>
                    )}
                    {isAddLocations && (
                      <Box sx={{marginTop: "16px", textAlign: "center"}}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "inline-block",
                            backgroundColor: "#C41E3A",
                            color: "#FFFFFF",
                            padding: "6px 16px",
                            borderRadius: "12px",
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            fontFamily: "'Merriweather', serif",
                          }}
                        >
                          Click to Add →
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Categories Showcase Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #C41E3A 0%, #DC143C 50%, #C41E3A 100%)",
          padding: {xs: "50px 20px", sm: "70px 40px", md: "90px 60px"},
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)",
            pointerEvents: "none",
          },
        }}
      >
        <Box sx={{maxWidth: "1200px", margin: "0 auto", position: "relative", zIndex: 1}}>
          <Typography
            variant="h2"
            sx={{
              fontSize: {xs: "2rem", sm: "2.5rem", md: "3rem"},
              fontWeight: 700,
              color: "#FAFAFA",
              textAlign: "center",
              marginBottom: 1,
              textShadow: "2px 2px 8px rgba(0, 0, 0, 0.3)",
            }}
          >
            Explore All Categories
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: {xs: "0.9rem", sm: "1.1rem"},
              color: "rgba(255, 255, 255, 0.9)",
              textAlign: "center",
              marginBottom: 5,
              fontWeight: 400,
            }}
          >
            Discover the magic of Christmas lights across different experiences
          </Typography>

          <Grid container spacing={3} sx={{justifyContent: "center"}}>
            {[
              { id: "house", iconPath: "/icons/house.png", label: "Houses", description: "Beautifully decorated homes" },
              { id: "event", iconPath: "/icons/events.png", label: "Events", description: "Festive celebrations" },
              { id: "community", iconPath: "/icons/community.png", label: "Community Lights", description: "Neighborhood displays" },
              { id: "market", iconPath: "/icons/market.png", label: "Markets", description: "Holiday shopping" },
              { id: "santa", iconPath: "/icons/santa.png", label: "Santa", description: "Meet Santa Claus" },
              { id: "tree_farm", iconPath: "/icons/christmastree_or_all.png", label: "Tree Farms", description: "Pick your tree" },
              { id: "show", iconPath: "/icons/shows.png", label: "Shows", description: "Live performances" },
              { id: "skating", iconPath: "/icons/skating.png", label: "Ice Skating", description: "Outdoor rinks" },
              { id: "restaurant", iconPath: "/icons/dining.png", label: "Dining", description: "Holiday meals" },
              { id: "shopping", iconPath: "/icons/market.png", label: "Shopping", description: "Gift destinations" },
              { id: "parade", iconPath: "/icons/parade.png", label: "Parades", description: "Colorful processions" },
              { id: "charity", iconPath: "/icons/charity.png", label: "Charity", description: "Give back" },
            ].map((category, index) => {
              return (
                <Grid item xs={6} sm={4} md={3} key={category.id}>
                  <Fade in timeout={400 + index * 100}>
                    <Box
                      onClick={() => navigate(`/map?category=${category.id}`)}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        padding: 3,
                        borderRadius: "20px",
                        background: "rgba(255, 255, 255, 0.1)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                        "&:hover": {
                          transform: "translateY(-8px) scale(1.05)",
                          background: "rgba(255, 255, 255, 0.2)",
                          border: "1px solid rgba(255, 255, 255, 0.5)",
                          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
                        },
                        "&:active": {
                          transform: "translateY(-4px) scale(1.02)",
                        },
                      }}
                    >
                      <Zoom in timeout={600 + index * 100}>
                        <Box
                          component="img"
                          src={category.iconPath}
                          alt={category.label}
                          sx={{
                            width: 72,
                            height: 72,
                            objectFit: "contain",
                            marginBottom: 2,
                            filter: "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4))",
                            transition: "transform 0.3s ease",
                            "&:hover": {
                              transform: "scale(1.1) rotate(5deg)",
                            },
                          }}
                        />
                      </Zoom>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#FAFAFA",
                          fontWeight: 700,
                          fontSize: {xs: "0.9rem", sm: "1rem"},
                          textAlign: "center",
                          marginBottom: 0.5,
                        }}
                      >
                        {category.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255, 255, 255, 0.8)",
                          fontSize: {xs: "0.7rem", sm: "0.75rem"},
                          textAlign: "center",
                          fontWeight: 400,
                        }}
                      >
                        {category.description}
                      </Typography>
                    </Box>
                  </Fade>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>

      {/* How It Works Section */}
      <Box
        sx={{
          backgroundColor: "#fff",
          padding: {xs: "60px 20px", sm: "80px 40px", md: "100px 60px"},
        }}
      >
        <Box sx={{maxWidth: "1200px", margin: "0 auto"}}>
          <Typography
            variant="h2"
            sx={{
            fontSize: {xs: "2rem", sm: "2.5rem", md: "3rem"},
            fontWeight: 700,
            textAlign: "center",
            marginBottom: "48px",
            color: "#C41E3A",
          }}
        >
          How It Works
        </Typography>

          <Grid container spacing={4} sx={{marginTop: "32px"}}>
            {steps.map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Fade in timeout={600 + index * 200}>
                  <Box
                    sx={{
                      textAlign: "center",
                      padding: "24px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-8px)",
                      },
                    }}
                  >
                    <Zoom in timeout={800 + index * 200}>
                      <Box
                        sx={{
                          width: "60px",
                          height: "60px",
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, #C41E3A 0%, #DC143C 100%)",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "1.5rem",
                          fontWeight: 700,
                          margin: "0 auto 24px",
                          boxShadow: "0 4px 12px rgba(196, 30, 58, 0.3)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "scale(1.1) rotate(5deg)",
                            boxShadow: "0 6px 20px rgba(196, 30, 58, 0.5)",
                          },
                        }}
                      >
                        {step.number}
                      </Box>
                    </Zoom>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        marginBottom: "12px",
                        color: "#333",
                        transition: "color 0.3s ease",
                      }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#666",
                        lineHeight: 1.6,
                      }}
                    >
                      {step.description}
                    </Typography>
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Add Your Display Section */}
      <Box
        sx={{
          background: "#FFFFFF",
          color: "#333",
          padding: {xs: "60px 20px", sm: "80px 40px", md: "100px 60px"},
          textAlign: "center",
          position: "relative",
        }}
      >
        <Box sx={{maxWidth: "1000px", margin: "0 auto"}}>
          <Typography
            variant="h2"
            sx={{
              fontSize: {xs: "2rem", sm: "2.5rem", md: "3rem"},
              fontWeight: 700,
              marginBottom: "16px",
              color: "#C41E3A",
              fontFamily: "'Merriweather', serif",
            }}
          >
            Share Your Christmas Magic
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: {xs: "1rem", sm: "1.15rem"},
              fontWeight: 400,
              marginBottom: "32px",
              color: "#666",
              lineHeight: 1.6,
              maxWidth: "700px",
              margin: "0 auto 32px",
            }}
          >
            Have a stunning Christmas light display? Know about a great holiday event? Share it with the community and help others discover the magic of the season.
          </Typography>

          <Grid container spacing={3} sx={{marginBottom: "40px", marginTop: "24px"}}>
            <Grid item xs={12} sm={4}>
              <Box
                onClick={() => navigate("/add-house")}
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "16px",
                  padding: "32px 24px",
                  border: "2px solid #E8E8E8",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  height: "100%",
                  "&:hover": {
                    borderColor: "#C41E3A",
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 24px rgba(196, 30, 58, 0.15)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "16px",
                  }}
                >
                  <HomeIcon sx={{ fontSize: "3rem", color: "#C41E3A" }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    marginBottom: "12px",
                    color: "#333",
                  }}
                >
                  Add Your House
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    fontSize: "0.95rem",
                    lineHeight: 1.6,
                  }}
                >
                  Showcase your beautiful Christmas light display to the community
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "16px",
                  padding: "32px 24px",
                  border: "2px solid #E8E8E8",
                  height: "100%",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "#C41E3A",
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 24px rgba(196, 30, 58, 0.15)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "16px",
                  }}
                >
                  <EventIcon sx={{ fontSize: "3rem", color: "#C41E3A" }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    marginBottom: "12px",
                    color: "#333",
                  }}
                >
                  Add Events
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    fontSize: "0.95rem",
                    lineHeight: 1.6,
                  }}
                >
                  Share markets, parades, and community events with others
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "16px",
                  padding: "32px 24px",
                  border: "2px solid #E8E8E8",
                  height: "100%",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "#C41E3A",
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 24px rgba(196, 30, 58, 0.15)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "16px",
                  }}
                >
                  <StarIcon sx={{ fontSize: "3rem", color: "#C41E3A" }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    marginBottom: "12px",
                    color: "#333",
                  }}
                >
                  Get Recognition
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    fontSize: "0.95rem",
                    lineHeight: 1.6,
                  }}
                >
                  Receive votes and reviews from the community
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              flexDirection: {xs: "column", sm: "row"},
              gap: "16px",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "32px",
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/add-house")}
              sx={{
                backgroundColor: "#C41E3A",
                color: "#FFFFFF",
                padding: "14px 40px",
                fontSize: "1.1rem",
                fontWeight: 600,
                borderRadius: "8px",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#DC143C",
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(196, 30, 58, 0.3)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Add Your Display →
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate("/map")}
              sx={{
                borderColor: "#C41E3A",
                color: "#C41E3A",
                padding: "14px 40px",
                fontSize: "1.1rem",
                fontWeight: 600,
                borderRadius: "8px",
                textTransform: "none",
                borderWidth: "2px",
                "&:hover": {
                  borderColor: "#DC143C",
                  backgroundColor: "rgba(196, 30, 58, 0.05)",
                  borderWidth: "2px",
                  color: "#DC143C",
                },
                transition: "all 0.3s ease",
              }}
            >
              Explore First
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Final CTA Section */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          color: "#333",
          padding: {xs: "80px 20px", sm: "100px 40px", md: "120px 60px"},
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box sx={{maxWidth: "1000px", margin: "0 auto"}}>
          {/* Badge */}
          <Box
            sx={{
              backgroundColor: "rgba(196, 30, 58, 0.08)",
              border: "1.5px solid rgba(196, 30, 58, 0.2)",
              borderRadius: "20px",
              padding: {xs: "10px 20px", sm: "12px 28px"},
              marginBottom: {xs: "40px", sm: "50px"},
              display: "inline-block",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Merriweather', serif",
                fontSize: {xs: "0.8rem", sm: "0.9rem"},
                fontWeight: 600,
                color: "#C41E3A",
                textTransform: "uppercase",
                letterSpacing: "2px",
              }}
            >
              Mobile App Launching Soon!
            </Typography>
          </Box>
          
          {/* Main Content with Santa and Heading */}
          <Box
            sx={{
              display: "flex",
              flexDirection: {xs: "column", sm: "row"},
              alignItems: "center",
              justifyContent: "center",
              gap: {xs: "24px", sm: "40px", md: "50px"},
              marginBottom: {xs: "40px", sm: "50px"},
            }}
          >
            <Box
              component="img"
              src="/cute_santa.png"
              alt="Santa"
              sx={{
                width: {xs: "220px", sm: "280px", md: "360px"},
                height: "auto",
                objectFit: "contain",
                display: "block",
                flexShrink: 0,
                order: {xs: 1, sm: 1},
              }}
            />
            <Box 
              sx={{ 
                flex: {xs: "none", sm: "0 0 auto"},
                order: {xs: 2, sm: 2},
                textAlign: {xs: "center", sm: "left"},
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontFamily: "'Merriweather', serif",
                  fontSize: {xs: "2.5rem", sm: "3.5rem", md: "4.25rem"},
                  fontWeight: 800,
                  background: "linear-gradient(135deg, #C41E3A 0%, #DC143C 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  lineHeight: 1.1,
                  marginBottom: {xs: "16px", sm: "20px"},
                  letterSpacing: "-0.5px",
                }}
              >
                Ready to Explore?
              </Typography>
            </Box>
          </Box>
          
          {/* Description Text */}
          <Box sx={{ marginBottom: {xs: "32px", sm: "40px"} }}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "'Merriweather', serif",
                fontSize: {xs: "1.5rem", sm: "1.85rem", md: "2.25rem"},
                fontWeight: 600,
                color: "#333",
                marginBottom: "20px",
                lineHeight: 1.3,
              }}
            >
              Discover the Magic of Christmas Lights
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: {xs: "1rem", sm: "1.1rem", md: "1.2rem"},
                fontWeight: 400,
                color: "#555",
                maxWidth: "750px",
                margin: "0 auto",
                lineHeight: 1.7,
              }}
            >
              Browse our interactive map to find stunning Christmas light displays near you. Discover hidden gems, vote for your favorites, and add your own displays to share the holiday spirit with your community.
            </Typography>
          </Box>
          
          {/* App Coming Soon Info */}
          <Box
            sx={{
              backgroundColor: "rgba(196, 30, 58, 0.05)",
              borderRadius: "16px",
              padding: {xs: "20px", sm: "24px 32px"},
              marginBottom: {xs: "40px", sm: "50px"},
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: {xs: "1.1rem", sm: "1.3rem"},
                fontWeight: 700,
                color: "#C41E3A",
                marginBottom: "12px",
              }}
            >
              Now Available on the App Store!
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Link 
                href="https://apps.apple.com/ca/app/christmas-light-map/id6755755089" 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ display: 'inline-block' }}
              >
                <img 
                  src="https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg" 
                  alt="Download on the App Store"
                  style={{ height: 45, width: 'auto' }}
                />
              </Link>
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: {xs: "0.85rem", sm: "0.95rem"},
                fontWeight: 400,
                color: "#666",
                lineHeight: 1.5,
                mb: 2
              }}
            >
              Enjoy enhanced features including:
            </Typography>
            <Box component="ul" sx={{ 
              textAlign: 'left', 
              pl: 3,
              '& li': {
                mb: 1,
                fontSize: {xs: "0.85rem", sm: "0.95rem"},
                color: "#666"
              }
            }}>
              <li>Offline access to all light displays</li>
              <li>Interactive route planning</li>
              <li>Push notifications for new displays</li>
              <li>Faster performance</li>
            </Box>
          </Box>
          
          {/* CTA Button */}
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/map")}
            sx={{
              backgroundColor: "#C41E3A",
              color: "#FFFFFF",
              padding: {xs: "14px 36px", sm: "18px 52px"},
              fontSize: {xs: "1.05rem", sm: "1.2rem"},
              fontWeight: 600,
              fontFamily: "'Inter', sans-serif",
              borderRadius: "12px",
              textTransform: "none",
              boxShadow: "0 4px 20px rgba(196, 30, 58, 0.25)",
              "&:hover": {
                backgroundColor: "#DC143C",
                transform: "translateY(-3px)",
                boxShadow: "0 8px 30px rgba(196, 30, 58, 0.35)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Launch Map →
          </Button>
        </Box>
      </Box>
    </Box>
    </ThemeProvider>
  );
};

export default LandingPage;
